/**
 * ShareholderMobileList - the phone view of the shareholder structure (Mobile.dc.html).
 *
 * An Owners (n) / Subsidiaries (n) toggle. Owners: the owner levels from the ultimate owners down to
 * Level 1 ('Level 3', 'Level 2', 'Level 1 · direct shareholders', by shortest distance to the
 * target), the aggregate row with 'Show all', and the target row at the bottom. Subsidiaries: the
 * target on top, then its subsidiaries. Below the owners, the notes of the Ultimate shareholders
 * table, which the phone layout leaves out: the ultimate owners the collapse hides ('Show all
 * owners') and a company without a majority shareholder. 'Open graph' opens the full-screen
 * popup. Rows use the same
 * CompactShareholderRow as the zoomed-out graph, so both phrase every node identically. No layout
 * engine runs here.
 *
 * @module ShareholderMobileList
 */
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import CompactShareholderRow from './graph/CompactShareholderRow';
import { FigureTooltip } from './graph/CardFigures';
import { DEFAULT_GRAPH_CONTEXT, GraphContext } from './graph/GraphContext';
import { GRAPH_COPY } from './graph/graphConstants';
import NoOwnersMessage from './graph/NoOwnersMessage';
import HiddenUltimatesNote from './HiddenUltimatesNote';
import IndependentShareholderNote from './IndependentShareholderNote';
import { buildLevelSections, LEVEL_SECTION } from './graph/shareholderLevels';
import { useRestoreNodeFocus } from './useRestoreNodeFocus';

export const MOBILE_VIEWS = { OWNERS: 'owners', SUBSIDIARIES: 'subsidiaries' };

const ViewToggle = ({ view, ownersCount, subsidiariesCount, onChange }) => (
  <fieldset className="sg-view-toggle" aria-label="Shareholder list">
    <button
      type="button"
      className="sg-view-toggle-button"
      aria-pressed={view === MOBILE_VIEWS.OWNERS}
      onClick={() => onChange(MOBILE_VIEWS.OWNERS)}
    >
      Owners ({ownersCount})
    </button>
    {subsidiariesCount > 0 && (
      <button
        type="button"
        className="sg-view-toggle-button"
        aria-pressed={view === MOBILE_VIEWS.SUBSIDIARIES}
        onClick={() => onChange(MOBILE_VIEWS.SUBSIDIARIES)}
      >
        Subsidiaries ({subsidiariesCount})
      </button>
    )}
  </fieldset>
);

ViewToggle.propTypes = {
  view: PropTypes.oneOf(Object.values(MOBILE_VIEWS)).isRequired,
  ownersCount: PropTypes.number.isRequired,
  subsidiariesCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
};

const Row = ({ node, onViewNodeDetails, onToggleNodeCollapsed, onShowAllFiltered }) => (
  <CompactShareholderRow
    node={node}
    variant="row"
    onActivate={
      node.card.navigable && onViewNodeDetails
        ? (activated) => onViewNodeDetails(activated.raw, activated)
        : undefined
    }
    onToggleCollapsed={onToggleNodeCollapsed}
    onShowAll={onShowAllFiltered}
  />
);

Row.propTypes = {
  node: PropTypes.object.isRequired,
  onViewNodeDetails: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func
};

// The target row stands alone (no heading, no row actions); every other section is a headed group.
const ListSection = ({ section, rowProps }) => {
  if (section.type === LEVEL_SECTION.TARGET) {
    return <CompactShareholderRow node={section.nodes[0]} variant="row" />;
  }
  return (
    <section className="sg-mobile-group">
      {section.heading && <h3 className="sg-mobile-group-heading">{section.heading}</h3>}
      {section.nodes.map((node) => (
        <Row key={node.id} node={node} {...rowProps} />
      ))}
    </section>
  );
};

ListSection.propTypes = {
  section: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(LEVEL_SECTION)).isRequired,
    heading: PropTypes.string,
    nodes: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  rowProps: PropTypes.shape({
    onViewNodeDetails: PropTypes.func,
    onToggleNodeCollapsed: PropTypes.func,
    onShowAllFiltered: PropTypes.func
  }).isRequired
};

const subsidiaryCountOf = (sections) =>
  sections.find((section) => section.type === LEVEL_SECTION.SUBSIDIARIES)?.nodes.length ?? 0;

/**
 * @param {Object} props
 * @param {Object|null} props.graph - PreparedGraph (null: no structure)
 * @param {'idle'|'loading'|'loaded'|'failed'} [props.figuresStatus]
 * @param {number} [props.percentageFilter] - for the no-owners message
 * @param {boolean} [props.isIndependent] - no majority shareholder: the note below the owners,
 *   or below the empty message without a structure
 * @param {string} [props.companyName] - the company that note names
 * @param {(raw, node) => void} [props.onViewNodeDetails] - same-tab navigation of a row
 * @param {(nodeId: string) => void} [props.onToggleNodeCollapsed] - '+N owners above'
 * @param {() => void} [props.onShowAllFiltered] - the aggregate row's 'Show all'
 * @param {() => void} [props.onExpandAllOwners] - 'Show all owners' below the owners
 * @param {() => void} [props.onTogglePopup] - 'Open graph' (full-screen popup)
 */
const ShareholderMobileList = ({
  graph = null,
  figuresStatus = 'loaded',
  percentageFilter,
  isIndependent = false,
  companyName = '',
  onViewNodeDetails,
  onToggleNodeCollapsed,
  onShowAllFiltered,
  onExpandAllOwners,
  onTogglePopup
}) => {
  const [selectedView, setSelectedView] = useState(MOBILE_VIEWS.OWNERS);
  const tooltipId = `sg-mobile-tooltip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const contextValue = useMemo(
    () => ({ ...DEFAULT_GRAPH_CONTEXT, tooltipId, figuresStatus, variant: 'row' }),
    [tooltipId, figuresStatus]
  );
  const ownerSections = useMemo(() => buildLevelSections(graph, { subsidiaries: false }), [graph]);
  const subsidiarySections = useMemo(() => buildLevelSections(graph, { owners: false }), [graph]);
  const listRef = useRef(null);
  const requestFocus = useRestoreNodeFocus(listRef, graph);
  // '+N owners above', 'Show all' and 'Show all owners' remove themselves: keep keyboard focus in
  // the list.
  const toggleCollapsed = useCallback(
    (nodeId) => {
      requestFocus(nodeId);
      onToggleNodeCollapsed(nodeId);
    },
    [requestFocus, onToggleNodeCollapsed]
  );
  const showAll = useCallback(() => {
    requestFocus(null);
    onShowAllFiltered();
  }, [requestFocus, onShowAllFiltered]);
  const expandAllOwners = useCallback(() => {
    requestFocus(null);
    onExpandAllOwners();
  }, [requestFocus, onExpandAllOwners]);

  if (!graph?.nodes?.length) {
    return (
      <div className="sg-mobile-list">
        <output className="sg-mobile-empty">{GRAPH_COPY.EMPTY}</output>
        {isIndependent && <IndependentShareholderNote companyName={companyName} />}
      </div>
    );
  }

  const subsidiariesCount = subsidiaryCountOf(subsidiarySections);
  const view = subsidiariesCount ? selectedView : MOBILE_VIEWS.OWNERS;
  const sections = view === MOBILE_VIEWS.SUBSIDIARIES ? subsidiarySections : ownerSections;
  const rowProps = {
    onViewNodeDetails,
    onToggleNodeCollapsed: onToggleNodeCollapsed ? toggleCollapsed : undefined,
    onShowAllFiltered: onShowAllFiltered ? showAll : undefined
  };

  return (
    <GraphContext.Provider value={contextValue}>
      <div className="sg-mobile-list" ref={listRef} tabIndex={-1}>
        <div className="sg-mobile-header">
          <ViewToggle
            view={view}
            ownersCount={graph.counts?.owners ?? 0}
            subsidiariesCount={subsidiariesCount}
            onChange={setSelectedView}
          />
          {onTogglePopup && (
            <IconButton
              accessible
              ariaLabel={GRAPH_COPY.OPEN_GRAPH}
              icon={ICON_BUTTON_ICONS.ARROW_ANGLE_EXPAND}
              type={ICON_BUTTON_TYPES.DEFAULT}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={GRAPH_COPY.OPEN_GRAPH}
              onClick={onTogglePopup}
            />
          )}
        </div>
        <p className="sg-mobile-hint">{GRAPH_COPY.MOBILE_HINT}</p>
        {view === MOBILE_VIEWS.OWNERS && (
          <NoOwnersMessage
            className="sg-mobile-empty"
            cause={graph.noOwnersCause}
            percentageFilter={percentageFilter}
          />
        )}
        {sections.map((section) => (
          <ListSection key={section.key} section={section} rowProps={rowProps} />
        ))}
        {view === MOBILE_VIEWS.OWNERS && (
          <>
            <HiddenUltimatesNote
              className="sg-mobile-note"
              count={graph.hiddenUltimateCount}
              onExpandAllOwners={onExpandAllOwners ? expandAllOwners : undefined}
            />
            {isIndependent && <IndependentShareholderNote companyName={companyName} />}
          </>
        )}
      </div>
      <FigureTooltip id={tooltipId} />
    </GraphContext.Provider>
  );
};

ShareholderMobileList.propTypes = {
  graph: PropTypes.object,
  figuresStatus: PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']),
  percentageFilter: PropTypes.number,
  isIndependent: PropTypes.bool,
  companyName: PropTypes.string,
  onViewNodeDetails: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func,
  onExpandAllOwners: PropTypes.func,
  onTogglePopup: PropTypes.func
};

export default ShareholderMobileList;
